import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const OBJECTIVES = ["Objetivo 1", "Objetivo 2", "Objetivo 3"];

const FIELDS = [
  { key: "progress_text", label: "¿Qué avances has hecho esta semana respecto a este objetivo?", type: "textarea" },
  { key: "progress_level", label: "Nivel de cumplimiento (1-10)", type: "slider" },
  { key: "obstacles_text", label: "¿Has encontrado obstáculos? ¿Cuáles?", type: "textarea" },
  { key: "obstacles_plan", label: "¿Cómo planeas superarlos esta semana?", type: "textarea" },
  { key: "satisfaction_level", label: "Nivel de satisfacción (1-10)", type: "slider" },
  { key: "satisfaction_plan", label: "¿Qué harás para aumentar tu satisfacción?", type: "textarea" },
  { key: "strategies", label: "¿Qué estrategias del programa MINDHOME HEALTH APP has utilizado?", type: "textarea" },
];

export default function RevisionStep({ sessionNumber, onDone }) {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  const update = (obj, field, value) => setData(d => ({ ...d, [obj]: { ...(d[obj] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/progress", { session_number: sessionNumber, step: "revision", completed: true, payload: data });
      toast.success("Revisión guardada");
      onDone(data);
    } catch {
      toast.error("No pudimos guardar la revisión.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">Repasa cómo te fue la semana con cada uno de tus 3 objetivos SMART. Sé honesto/a: los obstáculos también forman parte del proceso.</p>

      <div className="mt-8 space-y-6">
        {OBJECTIVES.map((obj, i) => (
          <Card key={obj} className="rounded-2xl border-[var(--ha-border)] bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-[var(--ha-secondary)]" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">{obj}</p>
              </div>
              <div className="space-y-4">
                {FIELDS.map(f => (
                  <div key={f.key}>
                    <label className="text-sm font-semibold text-[var(--ha-text)]">{f.label}</label>
                    {f.type === "textarea" ? (
                      <Textarea
                        data-testid={`rev-${sessionNumber}-${i}-${f.key}`}
                        value={data[obj]?.[f.key] || ""}
                        onChange={(e) => update(obj, f.key, e.target.value)}
                        className="mt-2 rounded-xl border-[var(--ha-border)] focus:border-[var(--ha-primary)] focus:ring-[var(--ha-primary)]"
                      />
                    ) : (
                      <div className="mt-3 flex items-center gap-4">
                        <Slider
                          data-testid={`rev-${sessionNumber}-${i}-${f.key}`}
                          value={[data[obj]?.[f.key] ?? 5]}
                          onValueChange={([v]) => update(obj, f.key, v)}
                          min={1} max={10} step={1}
                          className="flex-1"
                        />
                        <span className="text-sm font-bold text-[var(--ha-primary)] w-8 text-right">{data[obj]?.[f.key] ?? 5}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <Button data-testid="revision-continue-btn" onClick={save} disabled={saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Continuar <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
